import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { createGame, getBuildUploadUrl } from "../../services/developerService";
import { getCurrentUserProfile } from "../../services/dashboardService";
import { getTierByPlaytesterCount } from "../../utils/playtesterTiers";
import {
  PLAYTEST_TYPES,
  PRICING_URL,
  MAX_PLAYTESTERS,
  MIN_SESSION_HOURS,
  MAX_SESSION_HOURS,
  SESSION_HOURS_STEP,
  clampPlaytesterCount,
  clampSessionHours,
  estimatePlaytestCredits,
  formatHoursLabel,
  getPlaytestType,
} from "../../utils/playtestTypes";
import "./create-game-modal.scss";

const WEB_URL_EXAMPLE = "https://yourgame.com/play";
const DRAFT_STORAGE_PREFIX = "immersely.createGameDraft";

const DEFAULT_FORM_DATA = {
  name: "",
  genre: "",
  description: "",
  buildType: "file",
  buildFile: null,
  webUrl: "",
  accessToken: "",
  accessCredentials: {},
  playtestType: "free_private",
  numPlaytesters: 5,
  sessionHours: 2,
  lockTotalPlaytime: false,
  deadline: "",
  requirements: [],
  requirementsText: "",
};

function draftStorageKey(developerId) {
  return `${DRAFT_STORAGE_PREFIX}.${developerId || "anon"}`;
}

function loadCreateGameDraft(developerId) {
  try {
    const raw = sessionStorage.getItem(draftStorageKey(developerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearCreateGameDraft(developerId) {
  sessionStorage.removeItem(draftStorageKey(developerId));
}

function getWebUrlValidationMessage(value) {
  const trimmed = (value || "").trim();
  if (!trimmed) {
    return null;
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    return `Include http:// or https://. Example: ${WEB_URL_EXAMPLE}`;
  }
  try {
    const parsed = new URL(trimmed);
    if (!["http:", "https:"].includes(parsed.protocol)) {
      return `Use http:// or https://. Example: ${WEB_URL_EXAMPLE}`;
    }
    if (!parsed.hostname) {
      return `Enter a full address. Example: ${WEB_URL_EXAMPLE}`;
    }
    return null;
  } catch {
    return `Invalid web URL format. Use a full address like ${WEB_URL_EXAMPLE}`;
  }
}

function CreateGameModal({ developerId, initialCredits = null, onClose, onSuccess }) {
  const draft = useMemo(() => loadCreateGameDraft(developerId), [developerId]);
  const [step, setStep] = useState(() => {
    const savedStep = Number(draft?.step);
    return savedStep >= 1 && savedStep <= 3 ? savedStep : 1;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [webUrlTouched, setWebUrlTouched] = useState(false);
  const [creditsBalance, setCreditsBalance] = useState(initialCredits);

  const [formData, setFormData] = useState(() => ({
    ...DEFAULT_FORM_DATA,
    ...(draft?.formData || {}),
    buildFile: null,
  }));

  const selectedType = getPlaytestType(formData.playtestType);
  const estimate = useMemo(
    () =>
      estimatePlaytestCredits({
        playtestType: formData.playtestType,
        numPlaytesters: formData.numPlaytesters,
        sessionHours: formData.sessionHours,
        lockTotalPlaytime: formData.lockTotalPlaytime,
      }),
    [
      formData.playtestType,
      formData.numPlaytesters,
      formData.sessionHours,
      formData.lockTotalPlaytime,
    ]
  );

  const remainingCredits =
    creditsBalance == null ? null : creditsBalance - estimate.creditsRequired;
  const insufficientCredits =
    formData.playtestType === "paid_private" &&
    creditsBalance != null &&
    remainingCredits < 0;
  const webUrlError =
    formData.buildType === "web" ? getWebUrlValidationMessage(formData.webUrl) : null;

  useEffect(() => {
    let cancelled = false;
    getCurrentUserProfile().then((profile) => {
      if (cancelled || !profile) return;
      const credits = Number(profile.creditsEarned);
      setCreditsBalance(Number.isFinite(credits) ? credits : 0);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const { buildFile, ...rest } = formData;
    sessionStorage.setItem(
      draftStorageKey(developerId),
      JSON.stringify({
        step,
        formData: rest,
      })
    );
  }, [developerId, formData, step]);

  const isDirty = Boolean(
    (formData.name || "").trim() ||
    formData.genre ||
    (formData.description || "").trim() ||
    (formData.webUrl || "").trim() ||
    formData.buildFile ||
    formData.deadline ||
    (formData.requirementsText || "").trim() ||
    step > 1
  );

  const requestClose = () => {
    if (isDirty) {
      const shouldClose = window.confirm(
        "Close game setup? Your details stay saved on this device, so you can continue when you open Create Game again."
      );
      if (!shouldClose) return;
    }
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value, files, type, checked } = e.target;
    if (name === "buildFile") {
      setFormData({ ...formData, buildFile: files[0] });
    } else if (name === "requirementsText") {
      const requirements = value
        .split(/\r?\n/)
        .map((item) => item.trim())
        .filter(Boolean);
      setFormData({ ...formData, requirementsText: value, requirements });
    } else if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
    } else {
      if (name === "webUrl" || name === "buildType") {
        setWebUrlTouched(name === "webUrl" ? true : false);
        setError(null);
      }
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateBuildStep = () => {
    if (formData.buildType === "file" && !formData.buildFile) {
      setError("Please upload a game file");
      return false;
    }
    if (formData.buildType === "web") {
      setWebUrlTouched(true);
      if (!formData.webUrl?.trim()) {
        setError("Please provide a web URL");
        return false;
      }
      const message = getWebUrlValidationMessage(formData.webUrl);
      if (message) {
        setError(message);
        return false;
      }
    }
    setError(null);
    return true;
  };

  const goToPlaytesterStep = () => {
    if (!validateBuildStep()) return;
    setStep(3);
  };

  const handlePlaytestTypeChange = (typeId) => {
    const nextType = getPlaytestType(typeId);
    setFormData({
      ...formData,
      playtestType: typeId,
      numPlaytesters: nextType.canAssignPlaytesters
        ? clampPlaytesterCount(formData.numPlaytesters || nextType.defaultPlaytesters, typeId)
        : 0,
      sessionHours: nextType.canSetPlaytime ? formData.sessionHours || 2 : 2,
      lockTotalPlaytime: nextType.canSetPlaytime ? formData.lockTotalPlaytime : false,
    });
  };

  const handlePlaytesterCountChange = (count) => {
    setFormData({
      ...formData,
      numPlaytesters: clampPlaytesterCount(count, formData.playtestType),
    });
  };

  const handleSessionHoursChange = (hours) => {
    setFormData({ ...formData, sessionHours: clampSessionHours(hours) });
  };

  const resolveSignedUpload = async (file) => {
    const signedUrlResponse = await getBuildUploadUrl(developerId, {
      fileName: file.name,
      contentType: file.type || "application/octet-stream",
    });

    if (!signedUrlResponse?.uploadUrl || !signedUrlResponse?.publicUrl) {
      throw new Error("Failed to get an upload URL for the build file.");
    }

    return signedUrlResponse;
  };

  const startResumableUpload = async (uploadUrl, contentType) => {
    const response = await axios.post(uploadUrl, null, {
      headers: {
        "x-goog-resumable": "start",
        "Content-Type": contentType,
      },
      validateStatus: (status) => status >= 200 && status < 400,
    });

    const sessionUrl = response.headers?.location;
    if (!sessionUrl) {
      throw new Error("Upload session did not return a resumable URL.");
    }

    return sessionUrl;
  };

  const uploadBuildToStorage = async (file) => {
    const contentType = file.type || "application/octet-stream";
    const { uploadUrl, publicUrl } = await resolveSignedUpload(file);
    const sessionUrl = await startResumableUpload(uploadUrl, contentType);

    await axios.put(sessionUrl, file, {
      headers: {
        "Content-Type": contentType,
      },
    });

    return publicUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step !== 3) {
      return;
    }
    setLoading(true);
    setError(null);

    try {
      if (!formData.name || !formData.genre || !formData.description) {
        setError("Please fill in all required fields");
        setLoading(false);
        return;
      }

      if (formData.buildType === "file" && !formData.buildFile) {
        setError("Please upload a game file");
        setLoading(false);
        return;
      }

      if (formData.buildType === "web") {
        setWebUrlTouched(true);
        if (!formData.webUrl?.trim()) {
          setError("Please provide a web URL");
          setLoading(false);
          return;
        }
        const message = getWebUrlValidationMessage(formData.webUrl);
        if (message) {
          setError(message);
          setLoading(false);
          return;
        }
      }

      if (!formData.deadline) {
        setError("Please select a deadline");
        setLoading(false);
        return;
      }

      let buildFileUrl = "";
      if (formData.buildType === "file") {
        buildFileUrl = await uploadBuildToStorage(formData.buildFile);
      }

      const compatibleTier = getTierByPlaytesterCount(
        selectedType.canAssignPlaytesters ? formData.numPlaytesters : 5
      );

      const gameResponse = await createGame(developerId, {
        ...formData,
        numPlaytesters: selectedType.canAssignPlaytesters ? formData.numPlaytesters : 0,
        sessionHours: selectedType.canSetPlaytime ? formData.sessionHours : undefined,
        lockTotalPlaytime: selectedType.canSetPlaytime ? formData.lockTotalPlaytime : false,
        playtesterTier: compatibleTier.id,
        isPublicPlaytest: formData.playtestType === "free_public",
        creditsRequired: estimate.creditsRequired,
        deadline: new Date(formData.deadline),
        buildFileUrl,
      });

      clearCreateGameDraft(developerId);
      onSuccess(gameResponse);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to create game. Please try again.");
      setLoading(false);
    }
  };

  const submitLabel = loading
    ? "Creating..."
    : formData.playtestType === "free_public"
      ? "Create public playtest"
      : "Create game";

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="create-game-title">
      <div className="modal-content create-game-modal">
        <div className="modal-header">
          <div>
            <h2 id="create-game-title">Create New Game</h2>
            <p className="modal-header-sub">Step {step} of 3 · Progress is saved on this device</p>
          </div>
          <div className="modal-header-right">
            <div className="wallet-chip" title="Credits on your Immersely account">
              <span>Balance</span>
              <strong>
                {creditsBalance == null ? "…" : `${creditsBalance.toLocaleString()} credits`}
              </strong>
            </div>
            <button className="close-btn" onClick={requestClose} type="button" aria-label="Close">×</button>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA" && step !== 3) {
              e.preventDefault();
            }
          }}
        >
          {step === 1 && (
            <div className="form-step">
              <h3>Basic Information</h3>
              <div className="form-group">
                <label>Game Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter game name"
                />
              </div>
              <div className="form-group">
                <label>Genre *</label>
                <select
                  name="genre"
                  value={formData.genre}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Genre</option>
                  <option value="FPS">FPS</option>
                  <option value="MOBA">MOBA</option>
                  <option value="RPG">RPG</option>
                  <option value="Racing">Racing</option>
                  <option value="Sports">Sports</option>
                  <option value="Strategy">Strategy</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  required
                  placeholder="Describe your game..."
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setStep(2)} className="btn btn-primary">
                  Next: Build Setup
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="form-step">
              <h3>Build Setup</h3>
              <div className="form-group">
                <label>Build Type *</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="buildType"
                      value="file"
                      checked={formData.buildType === "file"}
                      onChange={handleInputChange}
                    />
                    <span>Upload Game File</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="buildType"
                      value="web"
                      checked={formData.buildType === "web"}
                      onChange={handleInputChange}
                    />
                    <span>Web Link</span>
                  </label>
                </div>
              </div>

              {formData.buildType === "file" && (
                <div className="form-group">
                  <label>Game Build File *</label>
                  <input
                    type="file"
                    name="buildFile"
                    onChange={handleInputChange}
                    accept=".exe,.zip,.rar,.7z"
                    required={formData.buildType === "file"}
                  />
                  <small>Supported formats: .exe, .zip, .rar, .7z (Max 10GB)</small>
                </div>
              )}

              {formData.buildType === "web" && (
                <>
                  <div className={`form-group ${webUrlTouched && webUrlError ? "has-error" : ""}`}>
                    <label>Game URL *</label>
                    <input
                      type="url"
                      name="webUrl"
                      value={formData.webUrl}
                      onChange={handleInputChange}
                      onBlur={() => setWebUrlTouched(true)}
                      placeholder={WEB_URL_EXAMPLE}
                      required={formData.buildType === "web"}
                      aria-invalid={webUrlTouched && Boolean(webUrlError)}
                    />
                    {webUrlTouched && webUrlError ? (
                      <small className="field-error">{webUrlError}</small>
                    ) : (
                      <small>Use a full URL including https://. Example: {WEB_URL_EXAMPLE}</small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Access Token (Optional)</label>
                    <input
                      type="text"
                      name="accessToken"
                      value={formData.accessToken}
                      onChange={handleInputChange}
                      placeholder="Token for playtester access"
                    />
                  </div>
                  <div className="form-group">
                    <label>Additional Credentials (JSON, Optional)</label>
                    <textarea
                      name="accessCredentials"
                      value={JSON.stringify(formData.accessCredentials, null, 2)}
                      onChange={(e) => {
                        try {
                          const parsed = JSON.parse(e.target.value);
                          setFormData({ ...formData, accessCredentials: parsed });
                        } catch (err) {
                          // Invalid JSON, keep as is
                        }
                      }}
                      rows={3}
                      placeholder='{"username": "test", "password": "test123"}'
                    />
                  </div>
                </>
              )}

              {error && <div className="error-message">{error}</div>}

              <div className="form-actions">
                <button type="button" onClick={() => setStep(1)} className="btn btn-secondary">
                  Back
                </button>
                <button type="button" onClick={goToPlaytesterStep} className="btn btn-primary">
                  Next: Playtester Setup
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="form-step">
              <h3>Playtester Configuration</h3>
              <p className="step-intro">
                Choose how this playtest is hosted. Player count and session length are only available on types that support them.
                Paid private playtests use the same credit formula as{" "}
                <a href={PRICING_URL} target="_blank" rel="noreferrer">
                  immersely.ai/pricing
                </a>
                : players × hours × 15.
              </p>

              <div className="form-group">
                <label>Playtest type *</label>
                <div className="playtest-type-list">
                  {PLAYTEST_TYPES.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      className={`playtest-type-card ${formData.playtestType === type.id ? "selected" : ""}`}
                      onClick={() => handlePlaytestTypeChange(type.id)}
                    >
                      <div className="playtest-type-card-top">
                        <h4>{type.name}</h4>
                        <span className={`playtest-price playtest-price--${type.priceLabel.toLowerCase()}`}>
                          {type.priceLabel}
                        </span>
                      </div>
                      <p className="playtest-type-summary">{type.summary}</p>
                      <ul className="playtest-outputs">
                        {type.outputs.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </button>
                  ))}
                </div>
              </div>

              {(selectedType.canAssignPlaytesters || selectedType.canSetPlaytime) && (
                <div className={`playtest-sizing ${!selectedType.canSetPlaytime ? "playtest-sizing--single" : ""}`}>
                  {selectedType.canAssignPlaytesters && (
                    <div className="form-group playtest-slider-group">
                      <div className="slider-label-row">
                        <label htmlFor="numPlaytesters">Number of playtesters</label>
                        <strong>
                          {formData.numPlaytesters}
                          {formData.playtestType === "paid_private" && formData.numPlaytesters >= MAX_PLAYTESTERS ? "+" : ""}
                        </strong>
                      </div>
                      <input
                        id="numPlaytesters"
                        type="range"
                        min={selectedType.minPlaytesters}
                        max={selectedType.maxPlaytesters}
                        step={1}
                        value={formData.numPlaytesters}
                        onChange={(e) => handlePlaytesterCountChange(e.target.value)}
                      />
                      <div className="slider-ends">
                        <span>{selectedType.minPlaytesters}</span>
                        <span>
                          {selectedType.maxPlaytesters}
                          {formData.playtestType === "paid_private" ? "+" : ""}
                        </span>
                      </div>
                    </div>
                  )}

                  {selectedType.canSetPlaytime && (
                    <div className="form-group playtest-slider-group">
                      <div className="slider-label-row">
                        <label htmlFor="sessionHours">
                          {formData.lockTotalPlaytime ? "Total playtime" : "Session length (per player)"}
                        </label>
                        <strong>{formatHoursLabel(formData.sessionHours)}</strong>
                      </div>
                      <input
                        id="sessionHours"
                        type="range"
                        min={MIN_SESSION_HOURS}
                        max={MAX_SESSION_HOURS}
                        step={SESSION_HOURS_STEP}
                        value={formData.sessionHours}
                        onChange={(e) => handleSessionHoursChange(e.target.value)}
                      />
                      <div className="slider-ends">
                        <span>{MIN_SESSION_HOURS}h</span>
                        <span>{MAX_SESSION_HOURS}h</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedType.canSetPlaytime && (
                <label className="lock-playtime">
                  <input
                    type="checkbox"
                    name="lockTotalPlaytime"
                    checked={formData.lockTotalPlaytime}
                    onChange={handleInputChange}
                  />
                  <span>
                    <strong>Lock in as total playtime</strong>
                    Treat the time above as the total playtime shared across all players, rather than the time each player plays.
                  </span>
                </label>
              )}

              <div className={`playtest-estimate ${insufficientCredits ? "playtest-estimate--warn" : ""}`}>
                <div className="playtest-estimate-main">
                  <span>Credit cap</span>
                  <strong>
                    {estimate.creditsRequired === 0
                      ? "0 · Free"
                      : `${estimate.creditsRequired.toLocaleString()} credits`}
                  </strong>
                </div>
                <dl className="playtest-estimate-meta">
                  {selectedType.canAssignPlaytesters && (
                    <div>
                      <dt>Playtesters</dt>
                      <dd>{estimate.players}</dd>
                    </div>
                  )}
                  {selectedType.canSetPlaytime && (
                    <>
                      <div>
                        <dt>{formData.lockTotalPlaytime ? "Total playtime" : "Playtime per player"}</dt>
                        <dd>{formatHoursLabel(formData.lockTotalPlaytime ? estimate.totalHours : estimate.hours)}</dd>
                      </div>
                      <div>
                        <dt>Total play hours</dt>
                        <dd>{estimate.totalHours}</dd>
                      </div>
                    </>
                  )}
                  <div>
                    <dt>Your balance</dt>
                    <dd>{creditsBalance == null ? "…" : creditsBalance.toLocaleString()}</dd>
                  </div>
                  {formData.playtestType === "paid_private" && (
                    <div>
                    <dt>Balance against cap</dt>
                    <dd>
                      {creditsBalance == null
                        ? "…"
                        : remainingCredits < 0
                          ? `${Math.abs(remainingCredits).toLocaleString()} below this cap`
                          : `${remainingCredits.toLocaleString()} under this cap`}
                    </dd>
                    </div>
                  )}
                </dl>
                {formData.playtestType === "paid_private" && (
                  <p className="playtest-estimate-note">
                    This is the credit cap for the playtest. Credits are not charged when you create the game.
                    {insufficientCredits ? (
                      <>
                        {" "}Your current balance is below this cap. You can{" "}
                        <a href="/settings" onClick={(e) => e.stopPropagation()}>
                          top up in Settings
                        </a>{" "}
                        later.
                      </>
                    ) : null}
                  </p>
                )}
                {formData.playtestType === "free_public" && (
                  <p className="playtest-estimate-note">
                    Playtesters join themselves. You cannot assign players or set playtime. Output is a basic report.
                  </p>
                )}
                {formData.playtestType === "free_private" && (
                  <p className="playtest-estimate-note">
                    Assign up to 10 playtesters. An admin will assign them after you create the game. Playtime is not configured for this type. Output is limited emotional signal.
                  </p>
                )}
              </div>

              <div className="form-group">
                <label>Playtesting Deadline *</label>
                <input
                  type="date"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split("T")[0]}
                  required
                />
              </div>

              <div className="form-group">
                <label>Requirements (one per line)</label>
                <textarea
                  name="requirementsText"
                  value={formData.requirementsText}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder={"Windows 10+\nNVIDIA GPU\nMinimum 8GB RAM"}
                />
                <small>These are sent as an array in the create request.</small>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="form-actions">
                <button type="button" onClick={() => setStep(2)} className="btn btn-secondary">
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {submitLabel}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default CreateGameModal;
